// The composer's sortable item list: pointer, touch and keyboard reordering (dnd-kit) with
// screen-reader announcements (Figma Composer body 4258:2229, Drop indicator 4257:2168).
import type { EmojiTarget } from "../../emoji/emojiTarget";
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors,
  type Announcements, type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, } from "@dnd-kit/sortable";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ComposerItemRow, { ComposerItemRowView } from "./ComposerItemRow";
import { MAX_ITEMS, moveItem, type ComposerItem } from "./composerModel";

export function reorderOnDragEnd(items: ComposerItem[], activeKey: number, overKey: number | null): ComposerItem[] {
  if (overKey == null || overKey === activeKey) return items;
  const from = items.findIndex((i) => i.key === activeKey);
  const to = items.findIndex((i) => i.key === overKey);
  if (from < 0 || to < 0) return items;
  return moveItem(items, from, to);
}

export interface ComposerItemListProps {
  items: ComposerItem[];
  personal: boolean;
  onReorder(items: ComposerItem[]): void;
  onRemove(key: number): void;
  onUrlChange(key: number, url: string): void;
  onTitleChange(key: number, title: string): void;
  onTitleReset(key: number): void;
  onRetry?(key: number): void;
  /** News: each row gets a caption field. */
  onCaptionChange?(key: number, caption: string): void;
  /** A caption field gained focus (for the composer's emoji button). */
  onCaptionFocus?(target: EmojiTarget): void;
}

export default function ComposerItemList(p: ComposerItemListProps) {
  const { items, personal } = p;
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<number | null>(null);
  const [overKey, setOverKey] = useState<number | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const pos = (id: unknown) => items.findIndex((i) => i.key === id) + 1;
  const announcements: Announcements = {
    onDragStart: ({ active }) => t("wall_reorder_picked", { defaultValue: "Picked up item {{position}} of {{total}}", position: pos(active.id), total: items.length }),
    onDragOver: ({ over }) => over ? t("wall_reorder_moved", { defaultValue: "Moved to position {{position}} of {{total}}", position: pos(over.id), total: items.length }) : undefined,
    onDragEnd: ({ over, active }) => t("wall_reorder_dropped", { defaultValue: "Dropped at position {{position}} of {{total}}", position: pos(over?.id ?? active.id), total: items.length }),
    onDragCancel: () => t("wall_reorder_cancelled", "Reordering cancelled"),
  };

  const end = (e: DragEndEvent) => {
    setActiveKey(null); setOverKey(null);
    const next = reorderOnDragEnd(items, Number(e.active.id), e.over ? Number(e.over.id) : null);
    if (next !== items) p.onReorder(next);
  };
  const moveBy = (index: number, d: -1 | 1) => p.onReorder(moveItem(items, index, index + d));
  const active = items.find((i) => i.key === activeKey);
  const lastKey = items[items.length - 1]?.key;

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center">
        <span className="flex-1 text-xs font-medium text-[var(--foreground-muted)]">
          {personal ? t("wall_items_label_personal", "Photos and videos · in this order") : t("wall_items_label", "Items · shown in this order")}
        </span>
        <span className="text-[11px] text-[var(--foreground-subtle)]">{t("wall_items_count", { defaultValue: "{{count}} / {{max}}", count: items.length, max: MAX_ITEMS })}</span>
      </div>
      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-[var(--border)] p-6 text-center">
          <span className="text-sm font-medium text-[var(--foreground)]">{t("wall_items_empty_title", "Nothing here yet")}</span>
          <span className="text-xs text-[var(--foreground-muted)]">
            {personal ? t("wall_items_empty_body_personal", "Add photos or videos and drag to set the order.") : t("wall_items_empty_body", "Add photos or paste a link. Mix them in any order and drag to reorder.")}
          </span>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          accessibility={{ announcements, screenReaderInstructions: { draggable: t("wall_reorder_instructions", "To pick up an item, press space or enter. Use the arrow keys to move it, space or enter to drop it, escape to cancel.") } }}
          onDragStart={(e: DragStartEvent) => setActiveKey(Number(e.active.id))}
          onDragOver={(e: DragOverEvent) => setOverKey(e.over ? Number(e.over.id) : null)}
          onDragEnd={end}
          onDragCancel={() => { setActiveKey(null); setOverKey(null); }}
        >
          <SortableContext items={items.map((i) => i.key)} strategy={() => null}>
            <div className="flex flex-col gap-2">
              {items.map((it, index) => (
                <div key={it.key} className="relative">
                  {activeKey != null && overKey === it.key && activeKey !== it.key && (
                    <div aria-hidden data-testid="drop-indicator" className={`absolute left-0 right-0 flex items-center ${index < items.findIndex((i) => i.key === activeKey) ? "-top-1.5" : "-bottom-1.5"}`}>
                      <span className="h-2.5 w-2.5 rounded-full border-2 border-[var(--primary-text)] bg-[var(--bg-surface)]" /><span className="h-0.5 flex-1 bg-[var(--primary)]" />
                    </div>
                  )}
                  <ComposerItemRow
                    item={it} index={index} total={items.length}
                    autoFocusUrl={it.kind === "link" && it.key === lastKey && it.status === "idle" && it.url === ""}
                    onRemove={() => p.onRemove(it.key)}
                    onUrlChange={(u) => p.onUrlChange(it.key, u)}
                    onTitleChange={(x) => p.onTitleChange(it.key, x)}
                    onTitleReset={() => p.onTitleReset(it.key)}
                    onRetry={p.onRetry ? () => p.onRetry!(it.key) : undefined}
                    onCaptionChange={p.onCaptionChange ? (c) => p.onCaptionChange!(it.key, c) : undefined}
                    onCaptionFocus={p.onCaptionFocus}
                    onMoveUp={() => moveBy(index, -1)}
                    onMoveDown={() => moveBy(index, 1)}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {active ? (
              <div className="rotate-[-1.5deg]"><ComposerItemRowView item={active} index={items.indexOf(active)} total={items.length} display lifted
                onRemove={() => {}} onUrlChange={() => {}} onTitleChange={() => {}} onTitleReset={() => {}} /></div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </section>
  );
}
